import { LexCode } from "../../../../library-js/dom-actions/LexCode";
import { ElementAttribute } from "../../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../../library-js/dom-tools/ElementType";
import { OnChange } from "../../../../library-js/dom-tools/EventListeners";
import { Range } from "../../../../library-js/geometry/Range";
import { RegExpUtility } from "../../../../library-js/text/RegExpUtility";
import { Arrays } from "../../../../library-js/tools/Arrays";

export class GDShaderStage
{
  index = -1;
  next:string = "";
  name:string;
  methodName:string;
  inputType:string[];
  outputType:string;

  static create( name:string, methodName:string, index:number, next:string )
  {
    let st = new GDShaderStage();
    st.name = name;
    st.methodName = methodName;
    st.index = index;
    st.next = next;
    st.inputType = [ "vec3" ];
    st.outputType = "vec3";

    return st;
  }

  setInput( inputType:string[] )
  {
    this.inputType = inputType;
    return this;
  }

  setOutput( outputType:string )
  {
    this.outputType = outputType;
    return this;
  }

  setInout( inOut:string )
  {
    return this.setInput( [ inOut ] ).setOutput( inOut );
  }
}


export class GDShaderStages
{
  static type = new ElementType( "gd-shader-stages" );

  static LOCAL  = GDShaderStage.create( "LOCAL/MODEL", "local", 0, "MODEL_MATRIX" );
  static GLOBAL = GDShaderStage.create( "GLOBAL/WORLD", "global", 1, "VIEW_MATRIX" ); 
  static VIEW   = GDShaderStage.create( "VIEW/CAMERA", "view", 2, "PROJECTION_MATRIX" ); 
  static CLIP   = GDShaderStage.create( "CLIP SPACE", "clip", 3, null, ).setInout( "vec4" ); 
  static SCREEN = GDShaderStage.create( "SCREEN SPACE","screen", 4, null ).setInput( ["vec2","float"] ).setOutput( "vec2" )
  ; 

  static stages = [ 
    GDShaderStages.LOCAL, 
    GDShaderStages.GLOBAL, 
    GDShaderStages.VIEW, 
    GDShaderStages.CLIP, 
    GDShaderStages.SCREEN
  ];

  static applyOnDocument()
  { 
    GDShaderStages.type.forAllInDoc( e => GDShaderStages.applyOnElement( e ) );
  }

  static applyOnElement( e:Element )
  {
    GDShaderStages.onChange( e, GDShaderStages.fromSpace, ()=>{ this.apply( e ) } );
    GDShaderStages.onChange( e, GDShaderStages.toSpace, ()=>{ this.apply( e ) } );
    GDShaderStages.onChange( e, GDShaderStages.vectorType, ()=>{ this.apply( e ) } );
    GDShaderStages.onChange( e, GDShaderStages.stage, ()=>{ this.apply( e ) } );
    GDShaderStages.onChange( e, GDShaderStages.optimization, ()=>{ this.apply( e ) } );

    this.apply( e );
  }

  static fromSpace = new ElementAttribute( "from-space" );
  static toSpace = new ElementAttribute( "to-space" );
  static vectorType = new ElementAttribute( "vector-type" );
  static stage = new ElementAttribute( "stage" );
  static optimization = new ElementAttribute( "optimization" );

  static getSelected( e:Element, att:ElementAttribute )
  {
    let sel = ( att.query( e ) as HTMLSelectElement );

    return sel.options[ sel.selectedIndex ].textContent;
  }

  static onChange( e:Element, att:ElementAttribute, callback:()=>void )
  {
    let sel = ( att.query( e ) as HTMLSelectElement );

    OnChange.add( sel, ()=>{ callback() });
  }


  static apply( e:Element )
  {
    let from = GDShaderStages.getSelected( e, GDShaderStages.fromSpace );
    let to = GDShaderStages.getSelected( e, GDShaderStages.toSpace );

    let fromStage = this.stages.find( s => s.name.toLowerCase() == from.toLowerCase() );
    let toStage  = this.stages.find( s => s.name.toLowerCase() == to.toLowerCase() );

    console.log( fromStage, toStage );

    let type = GDShaderStages.getSelected( e, GDShaderStages.vectorType );
    let stage = GDShaderStages.getSelected( e, GDShaderStages.stage );

    let opt = GDShaderStages.getSelected( e, GDShaderStages.optimization );

    let lexCode = LexCode.type.query( e );

    let methodName = `from_${fromStage.methodName}_to_${toStage.methodName}`;
    let inputVector = "point";
    let isDirection = type != "Point";

    

    let isVertexStage = stage == "Vertex Stage";
    let isOptimized = opt == "Full";

    let outputType = toStage.outputType;

    if ( isDirection )
    {
      inputVector = "direction";
      console.log( "Type is:", type ); 
    }

    inputVector = fromStage.methodName + "_" + inputVector;



    let parameters = "";
    
    
    for ( let i = 0; i < fromStage.inputType.length; i++ )
    {
      let parameterName = i == 0 ? inputVector : i == 1 ? "z" : ( "p" + ( i - 2 ) );     

      if ( i != 0 )
      {
        parameters += ", ";
      } 

      parameters += fromStage.inputType[ i ] + " " + parameterName;
    }

    let body = "";

    if ( isDirection && ( fromStage.index > 2 || toStage.index > 2 ) )
    {
      let outputTypeValue = outputType + "( 0.0 )"; 
      body = "// directions have no valid transformation to/from clip/screen space. \n    return " + outputTypeValue + ";"; 
    }
    else if ( fromStage.index == toStage.index )
    {
      body = "// no transformation\n    return " + inputVector + ";"; 
    }
    else
    {
      let w = isDirection ? "0.0" : "1.0";

      let matrices:string[] = [];

      let startIndex = Math.min( fromStage.index, toStage.index );
      let endIndex = Math.max( fromStage.index, toStage.index );

      let parameterAddition = "";     

      for ( let i = startIndex; i < endIndex; i++ )
      {
        let matrix = GDShaderStages.stages[ i ].next;

        if ( matrix == null )
        {
          continue;
        }

        matrices.push( matrix );
      }

      if ( isOptimized && isVertexStage )
      {
        let modelIndex = matrices.indexOf( GDShaderStages.LOCAL.next );
        let viewIndex = matrices.indexOf( GDShaderStages.GLOBAL.next ); 

        if ( modelIndex != -1 && viewIndex != -1 )
        {
          matrices.splice( modelIndex, 2, "MODELVIEW_MATRIX" );
        }
      }

      matrices = matrices.reverse();

      parameterAddition = matrices.map( m => ", mat4 _" + m ).join( "" );


      if ( fromStage.index < toStage.index )
      {
        body = "mat4 m = " + matrices.map( m => "_" + m ).join( " * " ) + ";"; 

        if ( fromStage.index == 3 && toStage.index == 4) 
        {
          body = `vec4 clip = ${inputVector};`;
          body += `\n    return ( clip.xy / clip.w ) * 0.5 + vec2( 0.5 );`;
        }
        else if ( toStage.index < 3 )
        {
          body += `\n    return ( m * vec4( ${inputVector}, ${w} ) ).xyz;`;
        } 
        else if ( toStage.index == 3 )
        {
          body += `\n    return m * vec4( ${inputVector}, ${w} );`;
        } 
        else
        {
          body += `\n    vec4 clip = m * vec4( ${inputVector}, ${w} );`;
          body += `\n    return ( clip.xy / clip.w ) * 0.5 + vec2( 0.5 );`;
        }        

      }
      else
      {
        let fromScreenToClip = fromStage.index == 4;

        let fromClipToView = fromStage.index >= 3 && toStage.index <= 2;

        let inverseProjectionMatrix = "inverse( _PROJECTION_MATRIX )";

        if ( isOptimized )
        {
          matrices = matrices.map( m => m == "PROJECTION_MATRIX" ? "INV_PROJECTION_MATRIX" : m );
          inverseProjectionMatrix = "INV_PROJECTION_MATRIX";
          parameterAddition = matrices.map( m => ", mat4 _" + m ).join( "" );
        }

        if ( fromScreenToClip  )
        {
          body += `\n    vec4 clip = vec4( ${inputVector} * 2.0 - 1.0, z, 1.0 );`;
          inputVector = "clip";
        }

        if ( fromClipToView )
        {
          body += `\n    vec4 view4 = ${inverseProjectionMatrix} * ${inputVector};`;
          body += `\n    view4 /= view4.w;` 
          body += `\n    vec3 view = view4.xyz;`    

          inputVector = "view";
          
        }

        if ( matrices.indexOf( GDShaderStages.VIEW.next ) != -1 )
        {
          matrices.splice( matrices.indexOf( GDShaderStages.VIEW.next ), 1 );
        }

        if ( matrices.indexOf( inverseProjectionMatrix ) != -1 )
        {
          matrices.splice( matrices.indexOf( inverseProjectionMatrix ), 1 );
        }

        

        if ( matrices.length > 0 )
        {          
          let inverseMatrix = "inverse( " + matrices.map( m => "_" + m ).join( " * " ) + " )";

          if ( isOptimized && inverseMatrix == "inverse( _VIEW_MATRIX )" )
          {
            inverseMatrix = "_INV_VIEW_MATRIX";
            parameterAddition = parameterAddition.replace( "VIEW_MATRIX", "INV_VIEW_MATRIX" );
          }  

          body += `\n    mat4 m = ${inverseMatrix};`;
          body += `\n    return ( m * vec4( ${inputVector}, ${w} ) ).xyz;`; 
        }
        else
        { 
          body += `\n    return ${inputVector};`; 
        }

      }

      parameters += parameterAddition;
    }

    

    
    let lines =
`
  ${outputType} ${methodName}( ${parameters} )
  {
    ${body}
  }

`;

   lexCode.innerHTML = lines.replace( /\n\s*\n/g, "\n");


   LexCode.applyOnElement( lexCode );

  } 
}